import { getJavaScriptExpressionForVariable } from './convertJavaScriptExpressionFromUser';
import { canonicalizeGetterCalls, scanGetterCalls } from './getterCalls';
import type { VariableInformation } from './types';

const INVALID = 'Invalid_';

const toIdentifier = (label: string): string =>
  label.replace(/\W+/g, '_').replace(/_+$/, '');

function resolveGetter(
  path: string,
  label: string | null,
  options: Options,
): string {
  if (
    label != null &&
    (label.startsWith(INVALID) || options.isForStepDescription)
  ) {
    return label;
  }

  const info = options.variables.find(
    ({ variableName, identifier = variableName }) => identifier === path,
  );

  if (info?.userIdentifier) {
    return info.userIdentifier;
  }

  return label == null
    ? `${INVALID}variable`
    : `${INVALID}${toIdentifier(label)}`;
}

interface Options {
  variables: VariableInformation[];
  isForStepDescription: boolean;
}

const DEFAULT_OPTIONS: Options = {
  variables: [],
  isForStepDescription: false,
};

/** Replace internal variable getters to user facing variable names */
export default function convertJavaScriptExpressionToUser(
  expression: string,
  optionsOrVariables: VariableInformation[] | Partial<Options>,
): string {
  const options: Options = Array.isArray(optionsOrVariables)
    ? { ...DEFAULT_OPTIONS, variables: optionsOrVariables }
    : { ...DEFAULT_OPTIONS, ...optionsOrVariables };

  let userExpression = canonicalizeGetterCalls(expression);

  for (const variable of options.variables) {
    if (variable.userIdentifier == null) {
      continue;
    }

    const jsExpression = getJavaScriptExpressionForVariable(variable);

    const escapedJsExpression = jsExpression.replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&',
    );

    userExpression = userExpression.replace(
      new RegExp(escapedJsExpression, 'g'),
      variable.userIdentifier,
    );
  }

  // One scanner for every reader of a stored expression: the same
  // `scanGetterCalls` the validator and the DSL parser use. A getter is
  // resolved by its path; the label only matters when the path is unknown,
  // and is then squeezed into one identifier so the user expression parses.
  const { calls, malformed } = scanGetterCalls(userExpression);

  const replacements = [
    ...calls.map((call) => ({
      index: call.index,
      text: call.text,
      replacement: resolveGetter(call.path, call.label, options),
    })),
    ...malformed.map((bad) => ({
      index: bad.index,
      text: bad.text,
      replacement: `${INVALID}variable`,
    })),
  ].sort((a, b) => b.index - a.index);

  for (const { index, text, replacement } of replacements) {
    userExpression =
      userExpression.slice(0, index) +
      replacement +
      userExpression.slice(index + text.length);
  }

  return userExpression;
}
